import { useEffect, useMemo, useRef, Fragment } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import { useFireEvent } from "@/components/cross-flow/touchpoint/utils";
import { LinkProps } from "@/components/wpl/link/src";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { LogoV2 } from "@/libraries/wpl/foundations/src";
import { ProductPageSubNav } from "@/patterns/global-nav/src";
import { LinkColumn } from "@/patterns/global-nav/src/layouts/subnav-link-columns";
import { GlobalNavLinkContentProps } from "@/patterns/global-nav/src/nav-links-container";
import { experiments, useExperiment } from "@/utils/experiment";

import { useAnalyticsClient } from "../../../../../../../analytics/wac-analytics/analytics-client";
import {
    DivOnHoverExit,
    GlobalNavProvider
} from "../../../../../../../patterns/global-nav/src/nav-links-container";

const JiraServiceManagementSoftwareNavV2 = () => {
    const intl = useIntl();
    const featuresHref = useLocalizedHref(
        "/software/jira/service-management/features"
    );
    const pricingHref = useLocalizedHref(
        "/software/jira/service-management/pricing"
    );
    const itSupportHref = useLocalizedHref(
        "/software/jira/service-management/itsm-software"
    );
    const itOperationsHref = useLocalizedHref(
        "/software/jira/service-management/it-operations"
    );
    const enterpriseServiceManagementHref = useLocalizedHref(
        "/software/jira/service-management/features/enterprise-service-management"
    );
    const customerServiceHref = useLocalizedHref(
        "/software/jira/service-management/customer-service-software"
    );
    const hrHref = useLocalizedHref("/software/jira/service-management/hr");
    const smallTeamsHref = useLocalizedHref(
        "/software/jira/service-management/help-desk-software-small-business-software"
    );
    const premiumHref = useLocalizedHref(
        "/software/jira/service-management/premium"
    );
    const enterpriseHref = useLocalizedHref(
        "/software/jira/service-management/enterprise"
    );
    const technologyTelecomHref = useLocalizedHref(
        "/software/jira/service-management/technology-telecommunications"
    );
    const financialServicesHref = useLocalizedHref(
        "/software/jira/service-management/financial-services"
    );
    const retailHref = useLocalizedHref(
        "/software/jira/service-management/retail-ecommerce"
    );
    const manufacturingHref = useLocalizedHref(
        "/software/jira/service-management/manufacturing"
    );
    const itsmHref = useLocalizedHref("/itsm");
    const productGuideHref = useLocalizedHref(
        "/software/jira/service-management/product-guide"
    );
    const resourcesHref = useLocalizedHref(
        "/software/jira/service-management/resources"
    );

    const dropdownStyles = useMemo(() => {
        return {
            width: "fit-content !important",
            margin: 0,
            insetBlockStart: "2.25rem",
            transform: "translateX(-50%)"
        };
    }, []);

    const experimentStatus = useExperiment(experiments.templatesTabInHeader);
    const templatesTabInHeaderCohort =
        experimentStatus.type === "enrolled"
            ? experimentStatus.value
            : experimentStatus.type;

    const templatesUrl = useLocalizedHref(
        "/software/jira/templates/service-management"
    );

    const { fireUIEvent } = useFireEvent();

    const viewedUIEvent = useRef(false);
    const analyticsClient = useAnalyticsClient();

    useEffect(() => {
        if (!viewedUIEvent.current && analyticsClient) {
            viewedUIEvent.current = true;
            fireUIEvent({
                action: "viewed",
                actionSubject: "jiraServiceManagementSoftwareNavV2",
                attributes: { templatesTabInHeaderCohort }
            });
        }
    }, [analyticsClient, fireUIEvent, templatesTabInHeaderCohort]);

    const links: GlobalNavLinkContentProps[] = useMemo(
        () => [
            {
                globalLinksKey: "features",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.features",
                    defaultMessage: "Features"
                }),
                hasSubnav: false,
                productKey: "jira-servicedesk.ondemand",
                href: featuresHref
            },
            {
                globalLinksKey: "pricing",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.pricing",
                    defaultMessage: "Pricing"
                }),
                hasSubnav: false,
                productKey: "jira-servicedesk.ondemand",
                href: pricingHref
            },
            ...(templatesTabInHeaderCohort === "variation"
                ? [
                      {
                          globalLinksKey: "templates",
                          linkText: intl.formatMessage({
                              id: "software-jira-service-management.navbar.templates",
                              defaultMessage: "Templates"
                          }),
                          hasSubnav: false,
                          productKey: "jira-servicedesk.ondemand",
                          href: templatesUrl
                      }
                  ]
                : []),
            {
                globalLinksKey: "solutions",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.solutions",
                    defaultMessage: "Solutions"
                }),
                hasSubnav: true,
                dropdownType: "solutions",
                productKey: "jira-servicedesk.ondemand",
                containerPosition: "relative",
                containerStyles: dropdownStyles,
                content: (
                    <DropdownContent
                        contentSections={[
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.solutions.useCases.header",
                                    defaultMessage: "By use case"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.itSupport",
                                            defaultMessage: "IT Support"
                                        }),
                                        href: itSupportHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.itOperations",
                                            defaultMessage: "IT Operations"
                                        }),
                                        href: itOperationsHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.businessTeams",
                                            defaultMessage: "Business Teams"
                                        }),
                                        href: enterpriseServiceManagementHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.customerService",
                                            defaultMessage: "Customer Service"
                                        }),
                                        href: customerServiceHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.humanResources",
                                            defaultMessage: "Human Resources"
                                        }),
                                        href: hrHref
                                    }
                                ]
                            },
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.solutions.capability.header",
                                    defaultMessage: "By capability"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.capability.smallTeams",
                                            defaultMessage: "Small Teams"
                                        }),
                                        href: smallTeamsHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.capability.premium",
                                            defaultMessage: "Premium"
                                        }),
                                        href: premiumHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.capability.enterprise",
                                            defaultMessage: "Enterprise"
                                        }),
                                        href: enterpriseHref
                                    }
                                ]
                            },
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.solutions.industry.customerService",
                                    defaultMessage: "By industry"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.industry.technologyTelecom",
                                            defaultMessage:
                                                "Technology & Telecom"
                                        }),
                                        href: technologyTelecomHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.industry.financialServices",
                                            defaultMessage: "Financial Services"
                                        }),
                                        href: financialServicesHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.industry.retail",
                                            defaultMessage: "Retail"
                                        }),
                                        href: retailHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.solutions.useCases.manufacturing",
                                            defaultMessage: "Manufacturing"
                                        }),
                                        href: manufacturingHref
                                    }
                                ]
                            }
                        ]}
                    />
                )
            },
            {
                globalLinksKey: "resources",
                linkText: intl.formatMessage({
                    id: "software-jira-service-management.navbar.resources",
                    defaultMessage: "Resources"
                }),
                hasSubnav: true,
                dropdownType: "resources",
                productKey: "jira-servicedesk.ondemand",
                containerPosition: "relative",
                containerStyles: dropdownStyles,
                content: (
                    <DropdownContent
                        contentSections={[
                            {
                                header: intl.formatMessage({
                                    id: "software-jira-service-management.navbar.resources.type.header",
                                    defaultMessage: "By type"
                                }),
                                links: [
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.resources.type.itsm",
                                            defaultMessage: "ITSM"
                                        }),
                                        href: itsmHref
                                    },
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.resources.type.productGuide",
                                            defaultMessage: "Product Guide"
                                        }),
                                        href: productGuideHref
                                    },
                                    ...(templatesTabInHeaderCohort !==
                                    "variation"
                                        ? [
                                              {
                                                  text: intl.formatMessage({
                                                      id: "software-jira-service-management.navbar.templates",
                                                      defaultMessage:
                                                          "Templates"
                                                  }),
                                                  href: templatesUrl
                                              }
                                          ]
                                        : []),
                                    {
                                        text: intl.formatMessage({
                                            id: "software-jira-service-management.navbar.resources.type.additionalResources",
                                            defaultMessage:
                                                "Additional Resources"
                                        }),
                                        href: resourcesHref
                                    }
                                ]
                            }
                        ]}
                    />
                )
            }
        ],
        [
            customerServiceHref,
            dropdownStyles,
            enterpriseHref,
            enterpriseServiceManagementHref,
            featuresHref,
            financialServicesHref,
            hrHref,
            intl,
            itOperationsHref,
            itSupportHref,
            itsmHref,
            manufacturingHref,
            premiumHref,
            pricingHref,
            productGuideHref,
            resourcesHref,
            retailHref,
            smallTeamsHref,
            technologyTelecomHref,
            templatesTabInHeaderCohort,
            templatesUrl
        ]
    );

    const signupLink = useLocalizedHref(
        "/software/jira/service-management/free"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_SERVICE_DESK,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });

    const globalNavItemKeys = useMemo(() => {
        return links.map((link) => link.globalLinksKey);
    }, [links]);

    return (
        <GlobalNavProvider globalNavItemKeys={globalNavItemKeys}>
            <ProductPageSubNav
                isTemplatesTabInHeader={
                    templatesTabInHeaderCohort === "variation"
                }
                productLogo={
                    <LogoV2
                        brand="jiraservicemanagement"
                        type="logo"
                        appearance="brand"
                    />
                }
                mobileLogo={
                    <LogoV2
                        brand="jiraservicemanagement"
                        type="logo"
                        appearance="brand"
                    />
                }
                productLogoUrl={useLocalizedHref(
                    "/software/jira/service-management"
                )}
                productLogoAriaLabel="Jira Service Management"
                productPageNavLinks={links}
                mobileLinksSections={[
                    {
                        mobileLinkKey: "mobile-jira-service-management",
                        mobileLinksProps: links
                    }
                ]}
                mobileCtaText={intl.formatMessage({
                    id: "productNavBar.get-it-free-button",
                    defaultMessage: "Get it free"
                })}
                mobileCtaUrl={signupLink}
                mobileCtaOnClick={openCrossFlow}
                navigationTrynowCtaText={intl.formatMessage({
                    id: "productNavBar.get-it-free-button",
                    defaultMessage: "Get it free"
                })}
                navigationTrynowCtaUrl={signupLink}
                navigationTrynowCtaOnClick={openCrossFlow}
                additionalMenuPageObjectsArray={[
                    {
                        menuPageKey: "solutions",
                        menuPageObjects: links.find(
                            (link) => link.globalLinksKey === "solutions"
                        )?.content as JSX.Element
                    },
                    {
                        menuPageKey: "resources",
                        menuPageObjects: links.find(
                            (link) => link.globalLinksKey === "resources"
                        )?.content as JSX.Element
                    }
                ]}
            />
        </GlobalNavProvider>
    );
};

interface DropdownContentProps {
    contentSections: DropdownContentSectionProps[];
    orientation?: "column" | "row";
}

export const DropdownContent = (props: DropdownContentProps) => {
    const { contentSections } = props;
    const { orientation = "row" } = props;

    return (
        <DivOnHoverExit>
            <div
                css={[
                    orientation === "row" && dropdownGridStyle,
                    orientation === "column" && dropdownColumnStyles
                ]}
            >
                {contentSections.map((section, idx) => (
                    <Fragment key={`${section.header}-${idx}`}>
                        <DropdownContentSection {...section} />
                        {orientation === "column" &&
                            idx < contentSections.length - 1 && (
                                <div css={dropdownColumnDividerStyles} />
                            )}
                    </Fragment>
                ))}
            </div>
        </DivOnHoverExit>
    );
};

interface DropdownContentSectionProps {
    header?: string;
    links: LinkProps[];
}

const DropdownContentSection = ({
    header,
    links
}: DropdownContentSectionProps) => {
    return (
        <LinkColumn>
            {header && <span css={headerStyle}>{header}</span>}
            <div css={linkColumnStyle}>
                {links.map((link) => (
                    <a
                        key={link.text}
                        href={link.href}
                        {...link.dataEventProps}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="global-Product-Nav-link-in-dropdown"
                        css={linkStyle}
                        onClick={link.onClick}
                    >
                        {link.text}
                    </a>
                ))}
            </div>
        </LinkColumn>
    );
};

const dropdownGridStyle = css({
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "32px",
    [media.above.md]: {
        padding: "40px",
        flexDirection: "row"
    }
});

const dropdownColumnStyles = css({
    padding: "20px",
    paddingBlockEnd: "40px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "16px"
});

const dropdownColumnDividerStyles = css({
    width: "100%",
    borderBlockStart: `solid 1px ${wacToken("color.neutral.dark1000")}`
});

const headerStyle = css({
    textTransform: "uppercase",
    color: wacToken("color.primary.black"),
    fontSize: "0.75rem",
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 700,
    lineHeight: "1rem"
});

const linkColumnStyle = css({
    display: "grid",
    gridAutoRows: "40px",
    width: "184px"
});

const linkStyle = css({
    "margin": 0,
    "color": "#101214",
    "fontSize": ".875rem",
    "alignSelf": "end",
    ":hover": {
        color: wacToken("color.extended.blue700")
    }
});

export default JiraServiceManagementSoftwareNavV2;
